import { useState } from 'react';
import { Loader2, Plus, X, BarChart2 } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ZAxis } from 'recharts';
import { api } from '../lib/gapApi';

export const MarketMatrix = ({ targetCompany }) => {
  const [competitors, setCompetitors] = useState(['']);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [matrixData, setMatrixData] = useState([]);
  const [error, setError] = useState('');

  const handleAddCompetitor = () => {
    if (competitors.length < 5) {
      setCompetitors([...competitors, '']);
    }
  };

  const handleRemoveCompetitor = (index) => {
    const newComps = [...competitors];
    newComps.splice(index, 1);
    setCompetitors(newComps);
  };

  const handleChange = (index, val) => {
    const newComps = [...competitors];
    newComps[index] = val;
    setCompetitors(newComps);
  };

  const handleRunMatrix = async () => {
    const validComps = competitors.filter(c => c.trim() !== '');
    if (!targetCompany) {
      setError("Please set the target company above first.");
      return;
    }
    if (validComps.length === 0) {
      setError("Please enter at least one competitor.");
      return;
    }

    setIsAnalyzing(true);
    setError('');

    try {
      const res = await api.post('/matrix', {
        target_company: targetCompany,
        competitors: validComps
      });
      setMatrixData(res.data.data);
    } catch (err) {
      setError("Failed to generate market matrix.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-800 border border-slate-600 p-4 rounded-xl shadow-xl">
          <p className="font-bold text-white mb-2">{data.name} {data.type === 'target' ? '(You)' : ''}</p>
          <p className="text-sm text-slate-300">Presence/SEO: <span className="text-emerald-400 font-bold">{data.presence}</span></p>
          <p className="text-sm text-slate-300">Sentiment: <span className="text-blue-400 font-bold">{data.sentiment}</span></p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-slate-900/60 backdrop-blur-xl rounded-3xl p-8 border border-indigo-500/30 shadow-xl mb-12 animate-in fade-in">
      <div className="flex items-center gap-3 mb-6 border-b border-slate-700/50 pb-4">
        <BarChart2 className="text-indigo-400" size={28} />
        <h2 className="text-2xl font-bold text-white">Multi-Competitor Market Matrix</h2>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-1 border-r border-slate-700/50 pr-8">
          <p className="text-slate-400 text-sm mb-4">Add up to 5 competitors to see where {targetCompany || 'your company'} stands in the market landscape.</p>

          <div className="space-y-3 mb-4">
            {competitors.map((comp, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder={`Competitor ${idx + 1}`}
                  value={comp}
                  onChange={(e) => handleChange(idx, e.target.value)}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-indigo-500 outline-none transition-colors text-sm"
                />
                {competitors.length > 1 && (
                  <button onClick={() => handleRemoveCompetitor(idx)} className="text-slate-500 hover:text-red-400">
                    <X size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {competitors.length < 5 && (
            <button onClick={handleAddCompetitor} className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center gap-1 mb-6">
              <Plus size={16} /> Add another
            </button>
          )}

          <button
            onClick={handleRunMatrix}
            disabled={isAnalyzing || !targetCompany}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
          >
            {isAnalyzing ? <><Loader2 className="animate-spin" size={18} /> Analyzing...</> : "Run Matrix Benchmark"}
          </button>
          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
        </div>

        <div className="md:col-span-2 h-[400px] flex flex-col">
          {matrixData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" dataKey="presence" name="Market Presence" domain={[0, 100]} stroke="#94a3b8" label={{ value: 'Market Presence & SEO', position: 'insideBottom', offset: -10, fill: '#94a3b8' }} />
                <YAxis type="number" dataKey="sentiment" name="Sentiment" domain={[0, 100]} stroke="#94a3b8" label={{ value: 'Customer Sentiment', angle: -90, position: 'insideLeft', fill: '#94a3b8' }} />
                <ZAxis type="number" range={[400, 400]} />
                <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                <Scatter name="Companies" data={matrixData}>
                  {matrixData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.type === 'target' ? '#10b981' : '#ef4444'} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex-1 flex items-center justify-center border-2 border-dashed border-slate-700/50 rounded-xl bg-slate-800/20">
              <p className="text-slate-500 italic">Run the benchmark to generate the Magic Quadrant.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
